import React, { useState, useEffect, useCallback } from 'react';
import {
  listVehicleTypes,
  createVehicleType,
  updateVehicleType,
  deleteVehicleType,
  getDeliverySettings,
  updateDeliverySettings,
  calculateDeliveryPrice,
} from '../services/adminDeliverySettingsService';
import './DeliverySettingsPage.css';

const fmt = (n) => {
  const v = parseFloat(n);
  return isNaN(v) ? '—' : v.toLocaleString('en-TZ');
};

const COMPLAINT_TYPES = ['Motorcycle', 'Bajaj / Tuk-Tuk', 'Car', 'Van / Pickup', 'Truck'];

// ─── Vehicle Type Form ────────────────────────────────────────────────────────
const VehicleTypeForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: '', code: '', description: '', baseFee: '', pricePerKm: '', maxWeightKg: '', isActive: true,
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.pricePerKm || parseFloat(form.pricePerKm) <= 0) { setError('Price per km must be > 0'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ds-form" onSubmit={handleSubmit}>
      {error && <div className="ds-form-error">{error}</div>}
      <div className="ds-form-grid">
        <div className="ds-field">
          <label>Vehicle Type Name *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Motorcycle" required />
        </div>
        <div className="ds-field">
          <label>Code</label>
          <input value={form.code} onChange={e => set('code', e.target.value)} placeholder="e.g. motorcycle" />
        </div>
        <div className="ds-field">
          <label>Base Fee (TZS)</label>
          <input type="number" min="0" value={form.baseFee} onChange={e => set('baseFee', e.target.value)} placeholder="2000" />
        </div>
        <div className="ds-field">
          <label>Price per km (TZS) *</label>
          <input type="number" min="0" value={form.pricePerKm} onChange={e => set('pricePerKm', e.target.value)} placeholder="500" required />
        </div>
        <div className="ds-field">
          <label>Max Weight (kg)</label>
          <input type="number" min="0" value={form.maxWeightKg} onChange={e => set('maxWeightKg', e.target.value)} placeholder="20" />
        </div>
        <div className="ds-field ds-field-wide">
          <label>Description</label>
          <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description" />
        </div>
        <div className="ds-field ds-field-toggle">
          <label>
            <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
            Active (visible to drivers)
          </label>
        </div>
      </div>
      <div className="ds-form-actions">
        <button type="button" className="ds-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="ds-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save vehicle type'}
        </button>
      </div>
    </form>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const DeliverySettingsPage = () => {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vehicle type form state
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Global settings form
  const [settingsForm, setSettingsForm] = useState({ defaultBaseFee: '', defaultPricePerKm: '', minFee: '', maxDistanceKm: '', currency: 'TZS', notes: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);

  // Price calculator
  const [calcKm, setCalcKm] = useState('');
  const [calcVehicle, setCalcVehicle] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [vt, s] = await Promise.all([listVehicleTypes(), getDeliverySettings()]);
      setVehicleTypes(vt);
      setSettings(s);
      if (s) {
        setSettingsForm({
          defaultBaseFee: s.defaultBaseFee ?? '',
          defaultPricePerKm: s.defaultPricePerKm ?? '',
          minFee: s.minFee ?? '',
          maxDistanceKm: s.maxDistanceKm ?? '',
          currency: s.currency || 'TZS',
          notes: s.notes || '',
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveVehicle = async (form) => {
    if (editingVehicle) {
      await updateVehicleType(editingVehicle.$id, form);
    } else {
      await createVehicleType(form);
    }
    setShowVehicleForm(false);
    setEditingVehicle(null);
    load();
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Delete this vehicle type? Existing delivery requests using it will not be affected.')) return;
    setDeletingId(id);
    try {
      await deleteVehicleType(id);
      load();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsMsg(null);
    try {
      await updateDeliverySettings(settingsForm);
      setSettingsMsg({ type: 'success', text: 'Settings saved successfully.' });
      load();
    } catch (err) {
      setSettingsMsg({ type: 'error', text: err.message || 'Failed to save' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const selectedVehicle = vehicleTypes.find(v => v.$id === calcVehicle);
  const calcPrice = calcKm && parseFloat(calcKm) > 0
    ? calculateDeliveryPrice(parseFloat(calcKm), selectedVehicle, settings)
    : null;

  if (loading) return <div className="ds-loading"><div className="ds-spinner" /><p>Loading delivery settings…</p></div>;
  if (error) return <div className="ds-error"><p>{error}</p><button onClick={load}>Retry</button></div>;

  return (
    <div className="ds-page">
      <div className="ds-page-header">
        <div>
          <h1>Delivery Settings</h1>
          <p className="ds-page-sub">Configure pricing, vehicle types and delivery rules</p>
        </div>
      </div>

      {/* ── Global Settings ── */}
      <section className="ds-section">
        <h2 className="ds-section-title">Global Defaults</h2>
        <p className="ds-section-desc">Fallback pricing when no vehicle-type-specific rate is configured.</p>
        <form className="ds-form" onSubmit={handleSaveSettings}>
          {settingsMsg && (
            <div className={`ds-form-${settingsMsg.type === 'success' ? 'success' : 'error'}`}>{settingsMsg.text}</div>
          )}
          <div className="ds-form-grid">
            <div className="ds-field">
              <label>Default Base Fee (TZS)</label>
              <input type="number" min="0" value={settingsForm.defaultBaseFee}
                onChange={e => setSettingsForm(f => ({ ...f, defaultBaseFee: e.target.value }))} placeholder="2000" />
            </div>
            <div className="ds-field">
              <label>Default Price / km (TZS)</label>
              <input type="number" min="0" value={settingsForm.defaultPricePerKm}
                onChange={e => setSettingsForm(f => ({ ...f, defaultPricePerKm: e.target.value }))} placeholder="500" />
            </div>
            <div className="ds-field">
              <label>Minimum Fee (TZS)</label>
              <input type="number" min="0" value={settingsForm.minFee}
                onChange={e => setSettingsForm(f => ({ ...f, minFee: e.target.value }))} placeholder="1500" />
            </div>
            <div className="ds-field">
              <label>Max Delivery Distance (km)</label>
              <input type="number" min="0" value={settingsForm.maxDistanceKm}
                onChange={e => setSettingsForm(f => ({ ...f, maxDistanceKm: e.target.value }))} placeholder="50" />
            </div>
            <div className="ds-field ds-field-wide">
              <label>Notes</label>
              <input value={settingsForm.notes}
                onChange={e => setSettingsForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes about pricing policy" />
            </div>
          </div>
          <div className="ds-form-actions">
            <button type="submit" className="ds-btn-primary" disabled={settingsSaving}>
              {settingsSaving ? 'Saving…' : 'Save global settings'}
            </button>
          </div>
        </form>
      </section>

      {/* ── Price Calculator ── */}
      <section className="ds-section">
        <h2 className="ds-section-title">Price Calculator</h2>
        <p className="ds-section-desc">Preview delivery price estimates with current rates.</p>
        <div className="ds-calc">
          <div className="ds-calc-inputs">
            <div className="ds-field">
              <label>Distance (km)</label>
              <input type="number" min="0" step="0.1" value={calcKm}
                onChange={e => setCalcKm(e.target.value)} placeholder="e.g. 5.5" />
            </div>
            <div className="ds-field">
              <label>Vehicle type</label>
              <select value={calcVehicle} onChange={e => setCalcVehicle(e.target.value)}>
                <option value="">— Default rates —</option>
                {vehicleTypes.filter(v => v.isActive).map(v => (
                  <option key={v.$id} value={v.$id}>{v.name}</option>
                ))}
              </select>
            </div>
          </div>
          {calcPrice != null && (
            <div className="ds-calc-result">
              <span className="ds-calc-label">Estimated Price</span>
              <span className="ds-calc-price">TZS {fmt(calcPrice)}</span>
              {selectedVehicle && (
                <span className="ds-calc-breakdown">
                  Base: TZS {fmt(selectedVehicle.baseFee)} + {fmt(calcKm)} km × TZS {fmt(selectedVehicle.pricePerKm)}/km
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Vehicle Types ── */}
      <section className="ds-section">
        <div className="ds-section-header-row">
          <div>
            <h2 className="ds-section-title">Vehicle Types</h2>
            <p className="ds-section-desc">Each vehicle type has its own pricing. Drivers select their type during registration.</p>
          </div>
          {!showVehicleForm && (
            <button className="ds-btn-primary" onClick={() => { setEditingVehicle(null); setShowVehicleForm(true); }}>
              + Add vehicle type
            </button>
          )}
        </div>

        {showVehicleForm && (
          <div className="ds-form-card">
            <h3>{editingVehicle ? 'Edit Vehicle Type' : 'New Vehicle Type'}</h3>
            <VehicleTypeForm
              initial={editingVehicle || {}}
              onSave={handleSaveVehicle}
              onCancel={() => { setShowVehicleForm(false); setEditingVehicle(null); }}
            />
          </div>
        )}

        <div className="ds-vehicle-grid">
          {vehicleTypes.length === 0 && <p className="ds-empty">No vehicle types configured yet.</p>}
          {vehicleTypes.map(v => (
            <div key={v.$id} className={`ds-vehicle-card ${!v.isActive ? 'ds-vehicle-card--inactive' : ''}`}>
              <div className="ds-vehicle-header">
                <span className="ds-vehicle-name">{v.name}</span>
                <span className={`ds-badge ${v.isActive ? 'ds-badge--active' : 'ds-badge--inactive'}`}>
                  {v.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="ds-vehicle-desc">{v.description || '—'}</p>
              <div className="ds-vehicle-rates">
                <div className="ds-rate-item">
                  <span className="ds-rate-label">Base fee</span>
                  <span className="ds-rate-value">TZS {fmt(v.baseFee)}</span>
                </div>
                <div className="ds-rate-item">
                  <span className="ds-rate-label">Per km</span>
                  <span className="ds-rate-value">TZS {fmt(v.pricePerKm)}</span>
                </div>
                <div className="ds-rate-item">
                  <span className="ds-rate-label">Max weight</span>
                  <span className="ds-rate-value">{v.maxWeightKg ? `${v.maxWeightKg} kg` : '—'}</span>
                </div>
              </div>
              <div className="ds-vehicle-actions">
                <button className="ds-btn-edit" onClick={() => { setEditingVehicle(v); setShowVehicleForm(true); }}>
                  Edit
                </button>
                <button className="ds-btn-delete" disabled={deletingId === v.$id}
                  onClick={() => handleDeleteVehicle(v.$id)}>
                  {deletingId === v.$id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DeliverySettingsPage;
